import React, { useState, useEffect } from 'react';
import {
  MagnifyingGlassIcon,
  TrashIcon,
  PencilIcon,
  UserIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XMarkIcon,
  ArrowPathIcon,
  ChartBarIcon,
  ClipboardDocumentListIcon,
  UserMinusIcon,
  EnvelopeIcon,
  UsersIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  InformationCircleIcon,
  BuildingOffice2Icon,
  CloudArrowUpIcon,
  ServerStackIcon,
} from '@heroicons/react/24/outline';
import HcsOrganizationsManager from '../components/HcsOrganizationsManager';
import AzureSyncManager from '../components/AzureSyncManager';
import StaleTenantCleanupPanel from '../components/StaleTenantCleanupPanel';

/**
 * GDPR Admin Page
 *
 * Administrative interface for user data management and GDPR compliance.
 * Provides functionality to:
 * - Search for users
 * - View detailed user information
 * - Update user records
 * - Delete user data (right to erasure)
 * - View system statistics
 * - View audit logs
 * - View deleted users (customer support)
 * - Manage HCS organizations (UC-016)
 * - Azure Table Storage sync (UC-017)
 */
const GdprAdminPage = () => {
  // Tab state
  const [activeTab, setActiveTab] = useState('allusers'); // 'allusers', 'users', 'organizations', 'azuresync', 'tenantcleanup', 'audit', 'deleted'

  // All Users state (read-only list)
  const [allUsers, setAllUsers] = useState([]);
  const [loadingAllUsers, setLoadingAllUsers] = useState(false);
  const [allUsersTotal, setAllUsersTotal] = useState(0);
  const [allUsersPagination, setAllUsersPagination] = useState({
    limit: 50,
    offset: 0,
    sortBy: 'created_at',
    sortOrder: 'desc'
  });

  // Search state
  const [searchEmail, setSearchEmail] = useState('');
  const [searchOrg, setSearchOrg] = useState('');
  const [searchMethod, setSearchMethod] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  // User detail state
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetail, setUserDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // Edit mode state
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);

  // Delete confirmation state
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [lastDeletionConfirmation, setLastDeletionConfirmation] = useState(null);

  // Statistics state
  const [stats, setStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);

  // Audit log state
  const [auditLog, setAuditLog] = useState([]);
  const [loadingAudit, setLoadingAudit] = useState(false);
  const [auditFilters, setAuditFilters] = useState({
    action_type: '',
    email: '',
    confirmation: '',
    days: 30
  });

  // Deleted users state
  const [deletedUsers, setDeletedUsers] = useState([]);
  const [loadingDeleted, setLoadingDeleted] = useState(false);
  const [deletedFilters, setDeletedFilters] = useState({
    email: '',
    confirmation: '',
    days: 90
  });

  // Messages
  const [message, setMessage] = useState({ type: '', text: '' });

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3050/api';

  // Clear message after 5 seconds
  useEffect(() => {
    if (message.text) {
      const timer = setTimeout(() => setMessage({ type: '', text: '' }), 5000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  // Fetch statistics on mount
  useEffect(() => {
    fetchStats();
    fetchAllUsers(); // Load all users on mount
  }, []);

  // Fetch data when tab changes
  useEffect(() => {
    if (activeTab === 'allusers' && allUsers.length === 0) {
      fetchAllUsers();
    } else if (activeTab === 'audit') {
      fetchAuditLog();
    } else if (activeTab === 'deleted') {
      fetchDeletedUsers();
    }
  }, [activeTab]);

  // Refetch all users when pagination changes
  useEffect(() => {
    if (activeTab === 'allusers') {
      fetchAllUsers();
    }
  }, [allUsersPagination]);

  const fetchAllUsers = async () => {
    setLoadingAllUsers(true);
    try {
      const params = new URLSearchParams();
      params.append('limit', allUsersPagination.limit);
      params.append('offset', allUsersPagination.offset);
      params.append('sort_by', allUsersPagination.sortBy);
      params.append('sort_order', allUsersPagination.sortOrder);

      const response = await fetch(`${API_URL}/gdpradm/users/list?${params.toString()}`);
      if (response.ok) {
        const data = await response.json();
        setAllUsers(data.users || []);
        setAllUsersTotal(data.total || 0);
      } else {
        setMessage({ type: 'error', text: 'Failed to load users list' });
      }
    } catch (error) {
      console.error('Error fetching all users:', error);
      setMessage({ type: 'error', text: 'Error connecting to server' });
    } finally {
      setLoadingAllUsers(false);
    }
  };

  const fetchStats = async () => {
    setLoadingStats(true);
    try {
      const response = await fetch(`${API_URL}/gdpradm/stats`);
      if (response.ok) {
        const data = await response.json();
        setStats(data.statistics);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoadingStats(false);
    }
  };

  const fetchAuditLog = async () => {
    setLoadingAudit(true);
    try {
      const params = new URLSearchParams();
      if (auditFilters.action_type) params.append('action_type', auditFilters.action_type);
      if (auditFilters.email) params.append('email', auditFilters.email);
      if (auditFilters.confirmation) params.append('confirmation', auditFilters.confirmation);
      params.append('days', auditFilters.days);

      const response = await fetch(`${API_URL}/gdpradm/audit-log?${params.toString()}`);
      if (response.ok) {
        const data = await response.json();
        setAuditLog(data.entries || []);
      }
    } catch (error) {
      console.error('Error fetching audit log:', error);
      setMessage({ type: 'error', text: 'Failed to load audit log' });
    } finally {
      setLoadingAudit(false);
    }
  };

  const fetchDeletedUsers = async () => {
    setLoadingDeleted(true);
    try {
      const params = new URLSearchParams();
      if (deletedFilters.email) params.append('email', deletedFilters.email);
      if (deletedFilters.confirmation) params.append('confirmation', deletedFilters.confirmation);
      params.append('days', deletedFilters.days);

      const response = await fetch(`${API_URL}/gdpradm/deleted-users?${params.toString()}`);
      if (response.ok) {
        const data = await response.json();
        setDeletedUsers(data.records || []);
      }
    } catch (error) {
      console.error('Error fetching deleted users:', error);
      setMessage({ type: 'error', text: 'Failed to load deleted users' });
    } finally {
      setLoadingDeleted(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    setSearching(true);
    setMessage({ type: '', text: '' });

    try {
      const params = new URLSearchParams();
      if (searchEmail) params.append('email', searchEmail);
      if (searchOrg) params.append('organization', searchOrg);
      if (searchMethod) params.append('registration_method', searchMethod);

      const response = await fetch(`${API_URL}/gdpradm/users/search?${params.toString()}`);
      const data = await response.json();

      if (response.ok) {
        setSearchResults(data.users || []);
        if (data.users.length === 0) {
          setMessage({ type: 'info', text: 'No users found matching your criteria' });
        }
      } else {
        setMessage({ type: 'error', text: data.detail || 'Search failed' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Error connecting to server' });
    } finally {
      setSearching(false);
    }
  };

  const handleSelectUser = async (user) => {
    setSelectedUser(user);
    setLoadingDetail(true);
    setEditMode(false);
    setDeleteConfirm(false);

    try {
      const response = await fetch(`${API_URL}/gdpradm/users/${user.user_id}`);
      const data = await response.json();

      if (response.ok) {
        setUserDetail(data);
        setEditData({
          email: data.user?.EMAIL || '',
          organization_name: data.user?.ORGANIZATION_NAME || '',
          email_verified: data.user?.EMAIL_VERIFIED || false,
          account_type: data.user?.ACCOUNT_TYPE || ''
        });
      } else {
        setMessage({ type: 'error', text: data.detail || 'Failed to load user details' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Error loading user details' });
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleSaveChanges = async () => {
    if (!selectedUser) return;
    setSaving(true);

    try {
      const response = await fetch(`${API_URL}/gdpradm/users/${selectedUser.user_id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editData)
      });

      const data = await response.json();

      if (response.ok) {
        setUserDetail(data);
        setEditMode(false);
        setMessage({ type: 'success', text: 'User updated successfully' });
        // Refresh search results
        if (searchEmail || searchOrg || searchMethod) {
          handleSearch({ preventDefault: () => {} });
        }
      } else {
        setMessage({ type: 'error', text: data.detail || 'Update failed' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Error saving changes' });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser || !deleteConfirm) return;
    setDeleting(true);

    try {
      const response = await fetch(
        `${API_URL}/gdpradm/users/${selectedUser.user_id}?confirm=true`,
        { method: 'DELETE' }
      );

      const data = await response.json();

      if (response.ok) {
        // Store confirmation number for display
        setLastDeletionConfirmation(data.confirmation_number);
        setMessage({
          type: 'success',
          text: `User deleted. Confirmation: ${data.confirmation_number}. Email sent: ${data.email_sent ? 'Yes' : 'No'}`
        });
        setSelectedUser(null);
        setUserDetail(null);
        setDeleteConfirm(false);
        // Remove from search results
        setSearchResults(prev => prev.filter(u => u.user_id !== selectedUser.user_id));
        // Refresh stats
        fetchStats();
      } else {
        setMessage({ type: 'error', text: data.detail || 'Delete failed' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Error deleting user' });
    } finally {
      setDeleting(false);
    }
  };

  const handleRevokeAllSessions = async () => {
    if (!selectedUser) return;

    try {
      const response = await fetch(
        `${API_URL}/gdpradm/users/${selectedUser.user_id}/sessions`,
        { method: 'DELETE' }
      );

      const data = await response.json();

      if (response.ok) {
        setMessage({ type: 'success', text: `Revoked ${data.sessions_revoked} sessions. Confirmation: ${data.confirmation_number}` });
        // Refresh user details
        handleSelectUser(selectedUser);
      } else {
        setMessage({ type: 'error', text: data.detail || 'Failed to revoke sessions' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Error revoking sessions' });
    }
  };

  return (
    <div className="gdpr-admin-page min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#0E3056] to-[#1a4a7a] text-white py-8">
        <div className="container mx-auto px-4">
          <h1 className="text-3xl font-bold">GDPR Admin Console</h1>
          <p className="text-gray-300 mt-2">User Data Management & Privacy Compliance</p>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="container mx-auto px-4">
          <nav className="flex space-x-8">
            <button
              onClick={() => setActiveTab('allusers')}
              className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center ${
                activeTab === 'allusers'
                  ? 'border-[#FFC000] text-[#FFC000]'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <UsersIcon className="h-5 w-5 mr-2" />
              All Users
            </button>
            <button
              onClick={() => setActiveTab('users')}
              className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center ${
                activeTab === 'users'
                  ? 'border-[#FFC000] text-[#FFC000]'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <UserIcon className="h-5 w-5 mr-2" />
              User Management
            </button>
            <button
              onClick={() => setActiveTab('organizations')}
              className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center ${
                activeTab === 'organizations'
                  ? 'border-[#FFC000] text-[#FFC000]'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <BuildingOffice2Icon className="h-5 w-5 mr-2" />
              HCS Organizations
            </button>
            <button
              onClick={() => setActiveTab('azuresync')}
              className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center ${
                activeTab === 'azuresync'
                  ? 'border-[#FFC000] text-[#FFC000]'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <CloudArrowUpIcon className="h-5 w-5 mr-2" />
              Azure Sync
            </button>
            <button
              onClick={() => setActiveTab('tenantcleanup')}
              className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center ${
                activeTab === 'tenantcleanup'
                  ? 'border-[#FFC000] text-[#FFC000]'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <ServerStackIcon className="h-5 w-5 mr-2" />
              Tenant Cleanup
            </button>
            <button
              onClick={() => setActiveTab('audit')}
              className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center ${
                activeTab === 'audit'
                  ? 'border-[#FFC000] text-[#FFC000]'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <ClipboardDocumentListIcon className="h-5 w-5 mr-2" />
              Audit Log
            </button>
            <button
              onClick={() => setActiveTab('deleted')}
              className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center ${
                activeTab === 'deleted'
                  ? 'border-[#FFC000] text-[#FFC000]'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <UserMinusIcon className="h-5 w-5 mr-2" />
              Deleted Users
            </button>
          </nav>
        </div>
      </div>

      {/* Message Banner */}
      {message.text && (
        <div className={`border-l-4 p-4 ${
          message.type === 'error' ? 'bg-red-50 border-red-500 text-red-700' :
          message.type === 'success' ? 'bg-green-50 border-green-500 text-green-700' :
          'bg-blue-50 border-blue-500 text-blue-700'
        }`}>
          <div className="container mx-auto px-4 flex items-center justify-between">
            <div className="flex items-center">
              {message.type === 'error' && <ExclamationTriangleIcon className="h-5 w-5 mr-2" />}
              {message.type === 'success' && <CheckCircleIcon className="h-5 w-5 mr-2" />}
              <span>{message.text}</span>
            </div>
            <button onClick={() => setMessage({ type: '', text: '' })}>
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}

      <div className="container mx-auto px-4 py-8">
        {/* All Users Tab (Read-only) */}
        {activeTab === 'allusers' && (
          <div className="space-y-6">
            {/* Header with Stats */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                    <UsersIcon className="h-5 w-5 mr-2 text-[#FFC000]" />
                    All Registered Users
                  </h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    Read-only view of all users in the system
                  </p>
                </div>
                <div className="flex items-center space-x-4">
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    Total: <span className="font-semibold text-[#FFC000]">{allUsersTotal}</span> users
                  </span>
                  <button
                    onClick={fetchAllUsers}
                    className="text-gray-500 hover:text-gray-700 p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"
                    disabled={loadingAllUsers}
                  >
                    <ArrowPathIcon className={`h-5 w-5 ${loadingAllUsers ? 'animate-spin' : ''}`} />
                  </button>
                </div>
              </div>

              {/* Sort Controls */}
              <div className="flex flex-wrap items-center gap-4 mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                <div className="flex items-center space-x-2">
                  <label className="text-sm text-gray-600 dark:text-gray-400">Sort by:</label>
                  <select
                    value={allUsersPagination.sortBy}
                    onChange={(e) => setAllUsersPagination(prev => ({ ...prev, sortBy: e.target.value, offset: 0 }))}
                    className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                  >
                    <option value="created_at">Date Created</option>
                    <option value="registered_at">Date Registered</option>
                    <option value="email">Email</option>
                    <option value="organization_name">Organization</option>
                    <option value="account_type">Account Type</option>
                  </select>
                </div>
                <div className="flex items-center space-x-2">
                  <label className="text-sm text-gray-600 dark:text-gray-400">Order:</label>
                  <select
                    value={allUsersPagination.sortOrder}
                    onChange={(e) => setAllUsersPagination(prev => ({ ...prev, sortOrder: e.target.value, offset: 0 }))}
                    className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                  >
                    <option value="desc">Newest First</option>
                    <option value="asc">Oldest First</option>
                  </select>
                </div>
                <div className="flex items-center space-x-2">
                  <label className="text-sm text-gray-600 dark:text-gray-400">Per page:</label>
                  <select
                    value={allUsersPagination.limit}
                    onChange={(e) => setAllUsersPagination(prev => ({ ...prev, limit: parseInt(e.target.value), offset: 0 }))}
                    className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                  >
                    <option value="25">25</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Users Table */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-900">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Email
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Organization
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Details
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {loadingAllUsers ? (
                      <tr>
                        <td colSpan="3" className="px-6 py-8 text-center">
                          <ArrowPathIcon className="h-6 w-6 animate-spin mx-auto text-gray-400" />
                          <p className="mt-2 text-gray-500">Loading users...</p>
                        </td>
                      </tr>
                    ) : allUsers.length === 0 ? (
                      <tr>
                        <td colSpan="3" className="px-6 py-8 text-center text-gray-500">
                          No users found
                        </td>
                      </tr>
                    ) : (
                      allUsers.map((user) => (
                        <tr key={user.user_id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="w-8 h-8 bg-[#0E3056] rounded-full flex items-center justify-center mr-3">
                                <UserIcon className="h-4 w-4 text-white" />
                              </div>
                              <div>
                                <div className="text-sm font-medium text-gray-900 dark:text-white">
                                  {user.email}
                                </div>
                                <div className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                                  {user.user_id?.substring(0, 8)}...
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                            {user.organization_name || <span className="text-gray-400">-</span>}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <div className="relative group inline-block">
                              <InformationCircleIcon className="h-5 w-5 text-[#FFC000] cursor-pointer hover:text-[#e6ad00]" />
                              {/* Hover Card */}
                              <div className="absolute right-0 z-50 invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-all duration-200 w-72 p-4 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 -translate-y-1/2 top-1/2 mr-6">
                                <div className="absolute right-[-8px] top-1/2 -translate-y-1/2 w-0 h-0 border-t-8 border-b-8 border-l-8 border-transparent border-l-white dark:border-l-gray-800"></div>
                                <h4 className="font-semibold text-gray-900 dark:text-white mb-3 pb-2 border-b border-gray-200 dark:border-gray-700">
                                  User Details
                                </h4>
                                <div className="space-y-2 text-sm">
                                  <div className="flex justify-between">
                                    <span className="text-gray-500 dark:text-gray-400">User ID:</span>
                                    <span className="font-mono text-xs text-gray-900 dark:text-white">{user.user_id?.substring(0, 12)}...</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-gray-500 dark:text-gray-400">Account Type:</span>
                                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                      user.account_type === 'admin' ? 'bg-purple-100 text-purple-800' :
                                      user.account_type === 'standard' ? 'bg-blue-100 text-blue-800' :
                                      'bg-gray-100 text-gray-800'
                                    }`}>{user.account_type || 'trial'}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-gray-500 dark:text-gray-400">Registration:</span>
                                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                      user.registration_method === 'email' ? 'bg-green-100 text-green-800' :
                                      user.registration_method === 'sso' ? 'bg-blue-100 text-blue-800' :
                                      user.registration_method === 'testing' ? 'bg-yellow-100 text-yellow-800' :
                                      'bg-gray-100 text-gray-800'
                                    }`}>{user.registration_method || 'local'}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-gray-500 dark:text-gray-400">Email Verified:</span>
                                    <span className={user.email_verified ? 'text-green-600' : 'text-yellow-600'}>{user.email_verified ? 'Yes' : 'No'}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-gray-500 dark:text-gray-400">Registered:</span>
                                    <span className="text-gray-900 dark:text-white">{user.registered_at ? new Date(user.registered_at).toLocaleDateString() : '-'}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-gray-500 dark:text-gray-400">Created:</span>
                                    <span className="text-gray-900 dark:text-white">{user.created_at ? new Date(user.created_at).toLocaleString() : '-'}</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {allUsersTotal > allUsersPagination.limit && (
                <div className="bg-gray-50 dark:bg-gray-900 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      Showing {allUsersPagination.offset + 1} to {Math.min(allUsersPagination.offset + allUsersPagination.limit, allUsersTotal)} of {allUsersTotal} users
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setAllUsersPagination(prev => ({ ...prev, offset: Math.max(0, prev.offset - prev.limit) }))}
                        disabled={allUsersPagination.offset === 0}
                        className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-gray-700"
                      >
                        <ChevronLeftIcon className="h-4 w-4" />
                      </button>
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        Page {Math.floor(allUsersPagination.offset / allUsersPagination.limit) + 1} of {Math.ceil(allUsersTotal / allUsersPagination.limit)}
                      </span>
                      <button
                        onClick={() => setAllUsersPagination(prev => ({ ...prev, offset: prev.offset + prev.limit }))}
                        disabled={allUsersPagination.offset + allUsersPagination.limit >= allUsersTotal}
                        className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-gray-700"
                      >
                        <ChevronRightIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Left Column - Search & Results */}
            <div className="lg:col-span-1 space-y-6">
              {/* Statistics Card */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                    <ChartBarIcon className="h-5 w-5 mr-2 text-[#FFC000]" />
                    Statistics
                  </h2>
                  <button
                    onClick={fetchStats}
                    className="text-gray-500 hover:text-gray-700"
                    disabled={loadingStats}
                  >
                    <ArrowPathIcon className={`h-5 w-5 ${loadingStats ? 'animate-spin' : ''}`} />
                  </button>
                </div>

                {stats ? (
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Total Users</span>
                      <span className="font-semibold text-gray-900 dark:text-white">{stats.total_users}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Active Sessions</span>
                      <span className="font-semibold text-gray-900 dark:text-white">{stats.active_sessions}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Verified</span>
                      <span className="font-semibold text-green-600">{stats.verification_status?.verified || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Unverified</span>
                      <span className="font-semibold text-yellow-600">{stats.verification_status?.unverified || 0}</span>
                    </div>
                    {stats.gdpr && !stats.gdpr.note && (
                      <>
                        <hr className="border-gray-200 dark:border-gray-700" />
                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-400">Deletions (30d)</span>
                          <span className="font-semibold text-red-600">{stats.gdpr.deletions_last_30_days || 0}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-400">Total Deleted</span>
                          <span className="font-semibold text-gray-900 dark:text-white">{stats.gdpr.total_deleted_users || 0}</span>
                        </div>
                      </>
                    )}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">Loading...</p>
                )}
              </div>

              {/* Search Form */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
                <h2 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white flex items-center">
                  <MagnifyingGlassIcon className="h-5 w-5 mr-2 text-[#FFC000]" />
                  Search Users
                </h2>

                <form onSubmit={handleSearch} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Email
                    </label>
                    <input
                      type="text"
                      value={searchEmail}
                      onChange={(e) => setSearchEmail(e.target.value)}
                      placeholder="Search by email..."
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Organization
                    </label>
                    <input
                      type="text"
                      value={searchOrg}
                      onChange={(e) => setSearchOrg(e.target.value)}
                      placeholder="Search by organization..."
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Registration Method
                    </label>
                    <select
                      value={searchMethod}
                      onChange={(e) => setSearchMethod(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                    >
                      <option value="">All Methods</option>
                      <option value="email">Email</option>
                      <option value="testing">Testing</option>
                      <option value="sso">SSO</option>
                      <option value="local">Local</option>
                    </select>
                  </div>

                  <button
                    type="submit"
                    disabled={searching}
                    className="w-full py-2 px-4 bg-[#0E3056] hover:bg-[#1a4a7a] text-white rounded-md font-medium transition-colors disabled:opacity-50"
                  >
                    {searching ? 'Searching...' : 'Search'}
                  </button>
                </form>
              </div>

              {/* Search Results */}
              {searchResults.length > 0 && (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
                  <h2 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
                    Results ({searchResults.length})
                  </h2>

                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {searchResults.map((user) => (
                      <button
                        key={user.user_id}
                        onClick={() => handleSelectUser(user)}
                        className={`w-full text-left p-3 rounded-md transition-colors ${
                          selectedUser?.user_id === user.user_id
                            ? 'bg-[#FFC000] bg-opacity-20 border-l-4 border-[#FFC000]'
                            : 'bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600'
                        }`}
                      >
                        <div className="flex items-center">
                          <UserIcon className="h-8 w-8 text-gray-400 mr-3" />
                          <div className="overflow-hidden">
                            <p className="font-medium text-gray-900 dark:text-white truncate">
                              {user.email}
                            </p>
                            <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                              {user.organization_name || 'No organization'}
                            </p>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Right Column - User Details */}
            <div className="lg:col-span-2">
              {selectedUser ? (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm">
                  {/* User Header */}
                  <div className="border-b border-gray-200 dark:border-gray-700 p-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="w-16 h-16 bg-[#0E3056] rounded-full flex items-center justify-center">
                          <UserIcon className="h-8 w-8 text-white" />
                        </div>
                        <div className="ml-4">
                          <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                            {selectedUser.email}
                          </h2>
                          <p className="text-gray-500 dark:text-gray-400">
                            ID: {selectedUser.user_id}
                          </p>
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => {
                            setEditMode(!editMode);
                            setDeleteConfirm(false);
                          }}
                          className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center"
                        >
                          <PencilIcon className="h-4 w-4 mr-2" />
                          {editMode ? 'Cancel' : 'Edit'}
                        </button>
                        <button
                          onClick={() => {
                            setDeleteConfirm(!deleteConfirm);
                            setEditMode(false);
                          }}
                          className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 flex items-center"
                        >
                          <TrashIcon className="h-4 w-4 mr-2" />
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Loading State */}
                  {loadingDetail ? (
                    <div className="p-8 text-center">
                      <ArrowPathIcon className="h-8 w-8 animate-spin mx-auto text-gray-400" />
                      <p className="mt-2 text-gray-500">Loading user details...</p>
                    </div>
                  ) : userDetail ? (
                    <>
                      {/* Delete Confirmation */}
                      {deleteConfirm && (
                        <div className="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 p-4 m-6">
                          <div className="flex items-start">
                            <ExclamationTriangleIcon className="h-6 w-6 text-red-500 mr-3" />
                            <div className="flex-1">
                              <h3 className="text-red-800 dark:text-red-200 font-semibold">
                                Confirm User Deletion
                              </h3>
                              <p className="text-red-700 dark:text-red-300 text-sm mt-1">
                                This action cannot be undone. All user data, sessions, and related records will be permanently deleted.
                              </p>
                              <p className="text-red-700 dark:text-red-300 text-sm mt-2">
                                <EnvelopeIcon className="h-4 w-4 inline mr-1" />
                                A confirmation email with a reference number will be sent to the user.
                              </p>
                              <div className="mt-4 flex space-x-3">
                                <button
                                  onClick={handleDeleteUser}
                                  disabled={deleting}
                                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
                                >
                                  {deleting ? 'Deleting...' : 'Yes, Delete User'}
                                </button>
                                <button
                                  onClick={() => setDeleteConfirm(false)}
                                  className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Edit Form or Details View */}
                      <div className="p-6 space-y-6">
                        {/* Core Profile Section */}
                        <div>
                          <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white border-b pb-2">
                            Core Profile
                          </h3>
                          {editMode ? (
                            <div className="grid md:grid-cols-2 gap-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                  Email
                                </label>
                                <input
                                  type="email"
                                  value={editData.email}
                                  onChange={(e) => setEditData({...editData, email: e.target.value})}
                                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                  Organization
                                </label>
                                <input
                                  type="text"
                                  value={editData.organization_name}
                                  onChange={(e) => setEditData({...editData, organization_name: e.target.value})}
                                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                  Account Type
                                </label>
                                <select
                                  value={editData.account_type}
                                  onChange={(e) => setEditData({...editData, account_type: e.target.value})}
                                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                                >
                                  <option value="trial">Trial</option>
                                  <option value="standard">Standard</option>
                                  <option value="admin">Admin</option>
                                </select>
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                  Email Verified
                                </label>
                                <select
                                  value={editData.email_verified ? 'true' : 'false'}
                                  onChange={(e) => setEditData({...editData, email_verified: e.target.value === 'true'})}
                                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                                >
                                  <option value="true">Verified</option>
                                  <option value="false">Not Verified</option>
                                </select>
                              </div>
                            </div>
                          ) : (
                            <div className="grid md:grid-cols-2 gap-4 text-sm">
                              <div>
                                <span className="text-gray-500 dark:text-gray-400">Email:</span>
                                <p className="font-medium text-gray-900 dark:text-white">{userDetail.user?.EMAIL}</p>
                              </div>
                              <div>
                                <span className="text-gray-500 dark:text-gray-400">Organization:</span>
                                <p className="font-medium text-gray-900 dark:text-white">{userDetail.user?.ORGANIZATION_NAME || '-'}</p>
                              </div>
                              <div>
                                <span className="text-gray-500 dark:text-gray-400">Account Type:</span>
                                <p className="font-medium text-gray-900 dark:text-white">{userDetail.user?.ACCOUNT_TYPE || '-'}</p>
                              </div>
                              <div>
                                <span className="text-gray-500 dark:text-gray-400">Email Verified:</span>
                                <p className={`font-medium ${userDetail.user?.EMAIL_VERIFIED ? 'text-green-600' : 'text-yellow-600'}`}>
                                  {userDetail.user?.EMAIL_VERIFIED ? 'Yes' : 'No'}
                                </p>
                              </div>
                              <div>
                                <span className="text-gray-500 dark:text-gray-400">Registration Method:</span>
                                <p className="font-medium text-gray-900 dark:text-white">{userDetail.user?.REGISTRATION_METHOD || '-'}</p>
                              </div>
                              <div>
                                <span className="text-gray-500 dark:text-gray-400">Registered At:</span>
                                <p className="font-medium text-gray-900 dark:text-white">
                                  {userDetail.user?.REGISTERED_AT ? new Date(userDetail.user.REGISTERED_AT).toLocaleString() : '-'}
                                </p>
                              </div>
                            </div>
                          )}

                          {editMode && (
                            <div className="mt-4 flex justify-end">
                              <button
                                onClick={handleSaveChanges}
                                disabled={saving}
                                className="px-6 py-2 bg-[#FFC000] text-[#0E3056] rounded-md font-semibold hover:bg-[#e6ad00] disabled:opacity-50"
                              >
                                {saving ? 'Saving...' : 'Save Changes'}
                              </button>
                            </div>
                          )}
                        </div>

                        {/* Sessions Section */}
                        <div>
                          <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white border-b pb-2">
                              Sessions ({userDetail.sessions?.length || 0})
                            </h3>
                            {userDetail.sessions?.length > 0 && (
                              <button
                                onClick={handleRevokeAllSessions}
                                className="text-sm text-red-600 hover:text-red-700"
                              >
                                Revoke All Sessions
                              </button>
                            )}
                          </div>
                          {userDetail.sessions?.length > 0 ? (
                            <div className="space-y-2 max-h-48 overflow-y-auto">
                              {userDetail.sessions.map((session, idx) => (
                                <div
                                  key={idx}
                                  className={`p-3 rounded-md text-sm ${
                                    session.IS_ACTIVE
                                      ? 'bg-green-50 dark:bg-green-900/20 border-l-4 border-green-500'
                                      : 'bg-gray-50 dark:bg-gray-700'
                                  }`}
                                >
                                  <div className="flex justify-between">
                                    <span className="text-gray-600 dark:text-gray-400">
                                      {session.IP_ADDRESS || 'Unknown IP'}
                                    </span>
                                    <span className={session.IS_ACTIVE ? 'text-green-600' : 'text-gray-500'}>
                                      {session.IS_ACTIVE ? 'Active' : 'Revoked'}
                                    </span>
                                  </div>
                                  <p className="text-xs text-gray-500 mt-1 truncate">
                                    {session.USER_AGENT || 'Unknown device'}
                                  </p>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-gray-500 dark:text-gray-400 text-sm">No sessions found</p>
                          )}
                        </div>

                        {/* NexusChat Profile Section */}
                        {userDetail.nexuschat_profile && (
                          <div>
                            <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white border-b pb-2">
                              NexusChat Profile
                            </h3>
                            <div className="grid md:grid-cols-2 gap-4 text-sm">
                              <div>
                                <span className="text-gray-500 dark:text-gray-400">Username:</span>
                                <p className="font-medium text-gray-900 dark:text-white">
                                  {userDetail.nexuschat_profile.USERNAME || '-'}
                                </p>
                              </div>
                              <div>
                                <span className="text-gray-500 dark:text-gray-400">Name:</span>
                                <p className="font-medium text-gray-900 dark:text-white">
                                  {userDetail.nexuschat_profile.NAME || '-'}
                                </p>
                              </div>
                              <div>
                                <span className="text-gray-500 dark:text-gray-400">Role:</span>
                                <p className="font-medium text-gray-900 dark:text-white">
                                  {userDetail.nexuschat_profile.ROLE || '-'}
                                </p>
                              </div>
                              <div>
                                <span className="text-gray-500 dark:text-gray-400">Provider:</span>
                                <p className="font-medium text-gray-900 dark:text-white">
                                  {userDetail.nexuschat_profile.PROVIDER || '-'}
                                </p>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Password Reset Tokens Section */}
                        {userDetail.password_reset_tokens?.length > 0 && (
                          <div>
                            <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white border-b pb-2">
                              Password Reset Tokens
                            </h3>
                            <div className="space-y-2">
                              {userDetail.password_reset_tokens.map((token, idx) => (
                                <div key={idx} className="p-3 bg-gray-50 dark:bg-gray-700 rounded-md text-sm">
                                  <div className="flex justify-between">
                                    <span className="text-gray-600 dark:text-gray-400">
                                      Created: {new Date(token.CREATED_AT).toLocaleString()}
                                    </span>
                                    <span className={token.USED ? 'text-gray-500' : 'text-yellow-600'}>
                                      {token.USED ? 'Used' : 'Active'}
                                    </span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </>
                  ) : null}
                </div>
              ) : (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-8 text-center">
                  <UserIcon className="h-16 w-16 mx-auto text-gray-300 dark:text-gray-600" />
                  <h2 className="mt-4 text-xl font-semibold text-gray-900 dark:text-white">
                    Select a User
                  </h2>
                  <p className="mt-2 text-gray-500 dark:text-gray-400">
                    Search for users and select one to view details
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Organizations Tab */}
        {activeTab === 'organizations' && (
          <HcsOrganizationsManager />
        )}

        {/* Azure Sync Tab (UC-017) */}
        {activeTab === 'azuresync' && (
          <AzureSyncManager />
        )}

        {/* Tenant Cleanup Tab (UC-018) */}
        {activeTab === 'tenantcleanup' && (
          <StaleTenantCleanupPanel />
        )}

        {/* Audit Log Tab */}
        {activeTab === 'audit' && (
          <div className="space-y-6">
            {/* Filters */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
              <h2 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white flex items-center">
                <ClipboardDocumentListIcon className="h-5 w-5 mr-2 text-[#FFC000]" />
                Audit Log Filters
              </h2>
              <div className="grid md:grid-cols-5 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Action Type
                  </label>
                  <select
                    value={auditFilters.action_type}
                    onChange={(e) => setAuditFilters({...auditFilters, action_type: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">All Actions</option>
                    <option value="USER_DELETED">User Deleted</option>
                    <option value="USER_UPDATED">User Updated</option>
                    <option value="SESSION_REVOKED">Session Revoked</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Email
                  </label>
                  <input
                    type="text"
                    value={auditFilters.email}
                    onChange={(e) => setAuditFilters({...auditFilters, email: e.target.value})}
                    placeholder="Search by email..."
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Confirmation #
                  </label>
                  <input
                    type="text"
                    value={auditFilters.confirmation}
                    onChange={(e) => setAuditFilters({...auditFilters, confirmation: e.target.value})}
                    placeholder="Search confirmation..."
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Days
                  </label>
                  <select
                    value={auditFilters.days}
                    onChange={(e) => setAuditFilters({...auditFilters, days: parseInt(e.target.value)})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                  >
                    <option value="7">Last 7 days</option>
                    <option value="30">Last 30 days</option>
                    <option value="90">Last 90 days</option>
                    <option value="365">Last year</option>
                  </select>
                </div>
                <div className="flex items-end">
                  <button
                    onClick={fetchAuditLog}
                    disabled={loadingAudit}
                    className="w-full py-2 px-4 bg-[#0E3056] hover:bg-[#1a4a7a] text-white rounded-md font-medium transition-colors disabled:opacity-50"
                  >
                    {loadingAudit ? 'Loading...' : 'Search'}
                  </button>
                </div>
              </div>
            </div>

            {/* Audit Log Table */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-900">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Confirmation #
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Action
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Target Email
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Organization
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Records
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Email Sent
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Date
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {loadingAudit ? (
                      <tr>
                        <td colSpan="7" className="px-6 py-8 text-center">
                          <ArrowPathIcon className="h-6 w-6 animate-spin mx-auto text-gray-400" />
                          <p className="mt-2 text-gray-500">Loading audit log...</p>
                        </td>
                      </tr>
                    ) : auditLog.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="px-6 py-8 text-center text-gray-500">
                          No audit entries found
                        </td>
                      </tr>
                    ) : (
                      auditLog.map((entry) => (
                        <tr key={entry.audit_id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-[#0E3056] dark:text-[#FFC000]">
                            {entry.confirmation_number}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              entry.action_type === 'USER_DELETED' ? 'bg-red-100 text-red-800' :
                              entry.action_type === 'USER_UPDATED' ? 'bg-blue-100 text-blue-800' :
                              'bg-yellow-100 text-yellow-800'
                            }`}>
                              {entry.action_type.replace('_', ' ')}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                            {entry.target_email || '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                            {entry.target_organization || '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                            {entry.records_affected}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {entry.email_sent ? (
                              <CheckCircleIcon className="h-5 w-5 text-green-500" />
                            ) : (
                              <XMarkIcon className="h-5 w-5 text-gray-400" />
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                            {entry.created_at ? new Date(entry.created_at).toLocaleString() : '-'}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Deleted Users Tab */}
        {activeTab === 'deleted' && (
          <div className="space-y-6">
            {/* Filters */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
              <h2 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white flex items-center">
                <UserMinusIcon className="h-5 w-5 mr-2 text-[#FFC000]" />
                Deleted Users Lookup (Customer Support)
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                Search for deleted users by email or confirmation number. Use this when customers contact support about their deleted accounts.
              </p>
              <div className="grid md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Email
                  </label>
                  <input
                    type="text"
                    value={deletedFilters.email}
                    onChange={(e) => setDeletedFilters({...deletedFilters, email: e.target.value})}
                    placeholder="Search by email..."
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Confirmation #
                  </label>
                  <input
                    type="text"
                    value={deletedFilters.confirmation}
                    onChange={(e) => setDeletedFilters({...deletedFilters, confirmation: e.target.value})}
                    placeholder="Search confirmation..."
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Days
                  </label>
                  <select
                    value={deletedFilters.days}
                    onChange={(e) => setDeletedFilters({...deletedFilters, days: parseInt(e.target.value)})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                  >
                    <option value="30">Last 30 days</option>
                    <option value="90">Last 90 days</option>
                    <option value="180">Last 6 months</option>
                    <option value="365">Last year</option>
                  </select>
                </div>
                <div className="flex items-end">
                  <button
                    onClick={fetchDeletedUsers}
                    disabled={loadingDeleted}
                    className="w-full py-2 px-4 bg-[#0E3056] hover:bg-[#1a4a7a] text-white rounded-md font-medium transition-colors disabled:opacity-50"
                  >
                    {loadingDeleted ? 'Loading...' : 'Search'}
                  </button>
                </div>
              </div>
            </div>

            {/* Deleted Users Table */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-900">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Confirmation #
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Email
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Organization
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Account Type
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Deleted At
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {loadingDeleted ? (
                      <tr>
                        <td colSpan="5" className="px-6 py-8 text-center">
                          <ArrowPathIcon className="h-6 w-6 animate-spin mx-auto text-gray-400" />
                          <p className="mt-2 text-gray-500">Loading deleted users...</p>
                        </td>
                      </tr>
                    ) : deletedUsers.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                          No deleted users found
                        </td>
                      </tr>
                    ) : (
                      deletedUsers.map((record) => (
                        <tr key={record.record_id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-[#0E3056] dark:text-[#FFC000]">
                            {record.confirmation_number}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                            {record.email}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                            {record.organization_name || '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                            {record.account_type || '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                            {record.deleted_at ? new Date(record.deleted_at).toLocaleString() : '-'}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GdprAdminPage;
